import { CallHistoryMethodAction, replace } from 'connected-react-router';
import { AccessToken, AccessTokenChangedError, AccessTokenResult, loadAccessToken } from '~/Api/Authentication';
import { passwordLogin, PasswordLoginResult } from '~/Api/Authentication/Password';
import { invokeApi, resolve } from '~/ApiClient';
import { handleError } from '~/ErrorHandler';
import { flashWarningMessage } from '~/FlashMessage/actions';
import logGoogleTagManagerUser from '~/GoogleTagManager/logGoogleTagManagerUser';
import { updateUserContext } from '~/Logger';
import { ThunkAction } from '~/Store';

import { Action, ActionType } from './Action';

export function login(email: string, password: string): ThunkAction<Promise<PasswordLoginResult>, Action> {
	return (dispatch, _, { persistentStateManager }) => {
		const request = dispatch(invokeApi(passwordLogin, email, password));
		dispatch({ type: ActionType.requestLogin, request });

		return resolve(request, (response) => {
			dispatch({
				type: ActionType.receiveLogin,
				request,
				accessToken: response?.accessToken ?? null,
			});

			if (response?.accessToken) {
				persistentStateManager.loadStateSafe(response.accessToken.user.id);
			}

			if (response !== null) {
				dispatch(invokeApi((apiClient) => apiClient.saveAccessToken(response.accessToken.token)));
				updateUserContext(response.accessToken);
				logGoogleTagManagerUser(response.accessToken.user);
			}
		});
	};
}

export function fetchAccessToken(): ThunkAction<Promise<AccessTokenResult>, Action> {
	return (dispatch, getState, { persistentStateManager }) => {
		const currentAccessToken = getState().accessToken.accessToken;
		const request = dispatch(invokeApi(loadAccessToken));
		dispatch({ type: ActionType.request, request });

		return resolve(request, (response) => {
			dispatch({
				type: ActionType.receive,
				request,
				accessToken: response?.accessToken ?? null,
				userChanged: Boolean(
					currentAccessToken !== null && currentAccessToken.user.id !== response?.accessToken.user.id
				),
			});

			if (response?.accessToken) {
				persistentStateManager.loadStateSafe(response.accessToken.user.id);
			}
		});
	};
}

export function fetchAccessTokenIfNeeded(): ThunkAction<Promise<AccessTokenResult>, Action> {
	return async (dispatch, getState) => {
		const { request } = getState().accessToken;
		if (request === null) {
			return dispatch(fetchAccessToken());
		}

		try {
			const cachedResponse = await request;
			if (cachedResponse.accessToken.token === dispatch(invokeApi((apiClient) => apiClient.loadAccessToken()))) {
				return cachedResponse;
			}

			return dispatch(fetchAccessToken()).then((response) => {
				const { user } = response.accessToken;
				if (user.id === cachedResponse.accessToken.user.id) {
					return response;
				}
				dispatch(flashWarningMessage(`You are now logged in as ${user.email}`));
				return Promise.reject(new AccessTokenChangedError());
			});
		} catch (e) {
			return dispatch(fetchAccessToken());
		}
	};
}

export function checkedFetchAccessTokenIfNeeded(): ThunkAction<Promise<AccessTokenResult | null>, Action> {
	return async (dispatch) => {
		try {
			return await dispatch(fetchAccessTokenIfNeeded());
		} catch (error) {
			if (!(error instanceof AccessTokenChangedError)) {
				dispatch(handleError(error));
			}
			return null;
		}
	};
}

// Loads the access token when present, but never redirects to login on failure.
// Used by navigation chrome that should work on public pages.
export function fetchAccessTokenSilently(): ThunkAction<Promise<AccessTokenResult | null>, Action> {
	return async (dispatch) => {
		try {
			return await dispatch(fetchAccessTokenIfNeeded());
		} catch (error) {
			if (error instanceof AccessTokenChangedError) {
				throw error;
			}
			return null;
		}
	};
}

export function logout(): ThunkAction<void, Action | CallHistoryMethodAction> {
	return (dispatch, _, { persistentStateManager }) => {
		persistentStateManager.persistCurrentUser();
		dispatch(invokeApi((apiClient) => apiClient.removeAccessToken()));
		updateUserContext(null);
		logGoogleTagManagerUser(null);
		dispatch({ type: ActionType.logout });
		dispatch(replace('/login'));
	};
}

export function invokeWithAccessToken<Result>(
	callback: (accessToken: AccessToken) => Result
): ThunkAction<Promise<Result>, Action> {
	return async (dispatch) => callback((await dispatch(fetchAccessTokenIfNeeded())).accessToken);
}
